import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { isHorizontal, transformToOrientation } from '@Components/D3Tree/utils';
import RsTree from '@Components/D3Tree/Tree/RsTree';

import { PATH_ROUTE_SVG, RouteType } from '@Pages/RetroSynthesis/constants';
import { ORIENTATION_RIGHT_TO_LEFT } from '@Components/D3Tree/constants';
import isEmpty from 'lodash/isEmpty';
import some from 'lodash/some';
import { usePrevious } from '@Pages/RetroSynthesis/ProjectRoute/hooks';
import { NODE_TYPE_SUBSTANCE, ZOOM_MAX, ZOOM_MIN, ZOOM_ONE } from '../constants';
import { drawDiagonalPath } from '../utils/pathFunc';
import { renderTreeNode } from '../utils/treeNodeRenderer';
import { SUBSTANCE_HALF_HEIGHT, SUBSTANCE_HALF_WIDTH, SUBSTANCE_HEIGHT, SUBSTANCE_WIDTH } from './SubstanceNode';
import { LINK_NODE_HORIZONTAL_WIDTH, LINK_NODE_VERTICAL_HEIGHT } from './LinkNode';

import './ProjectRouteTree.scss';

// 16px between sibling nodes
export const GAP_SIBLINGS = 16;
// distance between parent and child nodes
export const GAP_DEPTH = 48;

export const SEPARATION = {
  siblings: (GAP_SIBLINGS + SUBSTANCE_WIDTH) / SUBSTANCE_WIDTH,
  nonSiblings: (GAP_SIBLINGS + SUBSTANCE_WIDTH) / SUBSTANCE_WIDTH,
};

function getDepthFactor(orientation) {
  return isHorizontal(orientation)
    ? SUBSTANCE_WIDTH / 2 + LINK_NODE_HORIZONTAL_WIDTH / 2 + GAP_DEPTH
    : SUBSTANCE_HEIGHT / 2 + LINK_NODE_VERTICAL_HEIGHT / 2 + GAP_DEPTH;
}
/* eslint-disable react-hooks/exhaustive-deps */
function ProjectRouteTree({
  data,
  orientation,
  zoom,
  onZoomChanged,
  zoomEnabled,
  autoSize,
  structureImagesPath, // Structure images path from BE response
  activeStep,
  setActiveStep,
  conditionsIsShown,
  setConditionsIsShown,
  onDetailsClick,
  onActiveStepChange,
  treeId,
  nodesDisabled,
  routeType,
  addNodeEnabled,
  onAddNodeClick,
  openLinkOptions,
  shouldResize,
  onResized,
  leafPadding,
  displayStepConfidence,
}) {
  const treeRef = useRef();
  const depthFactor = getDepthFactor(orientation);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [treeSize, setTreeSize] = useState({});
  const [hoveredSubstanceNode, setHoveredSubstanceNode] = useState({});

  const prevActiveStep = usePrevious(activeStep);

  const onResizeLayout = ({ container, treeBoundaries }) => {
    const { width: containerWidth, height: containerHeight } = container;

    const treeWidth = treeBoundaries.xMax - treeBoundaries.xMin + SUBSTANCE_WIDTH;
    const treeHeight = treeBoundaries.yMax - treeBoundaries.yMin + SUBSTANCE_HEIGHT;
    const offsetX = Math.abs(treeBoundaries.xMin) + SUBSTANCE_WIDTH / 2;
    const offsetY = Math.abs(treeBoundaries.yMin) + SUBSTANCE_HEIGHT / 2;

    onResized();

    if (autoSize) {
      // Add padding equal substance size
      const width = (treeWidth + SUBSTANCE_WIDTH) * zoom;
      const height = (treeHeight + SUBSTANCE_HEIGHT) * zoom;
      setTreeSize({ width, height });
      const y = Math.round(offsetY * zoom + (SUBSTANCE_HEIGHT / 2) * zoom);
      const x = Math.round(width - SUBSTANCE_WIDTH * zoom);
      setTranslate({ x, y });
      return;
    }

    const zoomWidth = containerWidth / treeWidth;
    const zoomHeight = containerHeight / treeHeight;
    const adjustedZoom = Math.max(Math.min(zoomWidth, zoomHeight, ZOOM_ONE), ZOOM_MIN);

    if (adjustedZoom !== zoom) {
      onZoomChanged(adjustedZoom);
    }

    setTranslate({
      x: Math.round((containerWidth - treeWidth * adjustedZoom) / 2 + offsetX * adjustedZoom),
      y: Math.round((containerHeight - treeHeight * adjustedZoom) / 2 + offsetY * adjustedZoom),
    });
  };

  const onUpdateHandler = (event) => {
    if (!event) {
      return;
    }
    onZoomChanged(event.zoom);
    setTranslate(event.translate);
  };

  // useCallback prevents unnecessary updates for pure components
  // This is especially important for pathFunc and pathClassFunc!
  const pathFunc = useCallback(({ source, target }) => drawDiagonalPath({ source, target }, { orientation }), [orientation]);
  const pathClassFunc = useCallback(
    ({ source, target } = {}) => {
      let classNameStr = 'rt-project-route-tree__path';

      try {
        const activePath =
          activeStep &&
          (target?.data?._data?.parentStepNumber === activeStep || target?.data?._data?.group === activeStep) &&
          source?.data?._data?.step === activeStep;
        if (activePath) {
          classNameStr = 'rt-project-route-tree__active-path';
        } else if (target?.data?._data?.type === RouteType.PUBLISHED) {
          classNameStr = 'rt-project-route-tree__published-path';
        } else if (target?.data?._data?.type === RouteType.PREDICTED) {
          classNameStr = 'rt-project-route-tree__predicted-path';
        }

        if (
          source?.data?._data?.multiStep ||
          target?.data?._data?.multiStep ||
          source?.data?._data?.multiStepEnrolled ||
          target?.data?._data?.multiStepEnrolled
        ) {
          classNameStr = cn(classNameStr, 'rt-project-route-tree__multi-step-path');
        }

        return classNameStr;
      } catch (e) {
        Logger.error('TreeView Path function wrong format', e);
        return `rt-project-route-tree__path`;
      }
    },
    [activeStep]
  );

  const onHoverSubstanceNode = (isHovered, node) => {
    if (isHovered) {
      setHoveredSubstanceNode(node);
    } else {
      setHoveredSubstanceNode({});
    }
  };

  const renderCustomNodeElement = useCallback(
    ({ nodeDatum }) =>
      renderTreeNode(nodeDatum, {
        orientation,
        structureImagesPath,
        activeStep,
        setActiveStep,
        conditionsIsShown,
        setConditionsIsShown,
        onDetailsClick,
        onActiveStepChange,
        nodesDisabled,
        routeType,
        addNodeEnabled,
        onAddNodeClick,
        openLinkOptions,
        displayStepConfidence,
        onHoverSubstanceNode,
        hoveredSubstanceNode,
      }),
    [orientation, activeStep, conditionsIsShown, hoveredSubstanceNode]
  );

  const getHighlightedNodes = (activeNodes, requiredZoom) => {
    let highlightedNodes = { ax: Infinity, ay: Infinity, bx: -Infinity, by: -Infinity };
    activeNodes.forEach((node) => {
      const { x, y } = transformToOrientation(node, orientation);
      highlightedNodes = {
        ax: Math.round(Math.min(x, highlightedNodes.ax)),
        ay: Math.round(Math.min(y, highlightedNodes.ay)),
        bx: Math.round(Math.max(x, highlightedNodes.bx)),
        by: Math.round(Math.max(y, highlightedNodes.by)),
      };
    });
    highlightedNodes.ax -= SUBSTANCE_HALF_WIDTH + GAP_DEPTH;
    highlightedNodes.ax *= requiredZoom;
    highlightedNodes.bx += SUBSTANCE_HALF_WIDTH + GAP_DEPTH;
    highlightedNodes.bx *= requiredZoom;
    highlightedNodes.ay -= SUBSTANCE_HALF_HEIGHT + GAP_DEPTH;
    highlightedNodes.ay *= requiredZoom;
    highlightedNodes.by += SUBSTANCE_HALF_HEIGHT + GAP_DEPTH;
    highlightedNodes.by *= requiredZoom;

    return highlightedNodes;
  };

  const getTranslateXY = (highlightedNodes, containerSize) => {
    const { width: containerWidth, height: containerHeight } = containerSize;

    const tx = Math.round(translate.x);
    const ty = Math.round(translate.y);

    const increaseTy = Math.abs(Math.min(ty + highlightedNodes.ay, 0));
    const decreaseTy = Math.max(highlightedNodes.by + ty - containerHeight, 0);

    const increaseTx = Math.abs(Math.min(tx + highlightedNodes.ax, 0));
    const decreaseTx = Math.max(highlightedNodes.bx + tx - containerWidth, 0);

    const deltaY = Math.round(increaseTy - decreaseTy);
    const deltaX = Math.round(increaseTx - decreaseTx);
    return { deltaY, deltaX };
  };

  useLayoutEffect(() => {
    if (!prevActiveStep) {
      return;
    }
    if (!treeRef.current || isEmpty(treeRef.current.nodes)) {
      return;
    }
    const { nodes, containerSize } = treeRef.current || [];
    if (isEmpty(nodes)) {
      return;
    }

    const activeNodes = nodes.filter((node) => {
      const { _type } = node.data;
      const { step, parentStepNumber, group, groups = [] } = node.data._data;
      return (
        step === activeStep ||
        group === activeStep ||
        (_type === NODE_TYPE_SUBSTANCE && (parentStepNumber === activeStep || some(groups, (g) => g.id === activeStep)))
      );
    });

    let highlightedNodes = getHighlightedNodes(activeNodes, zoom);

    const { width: containerWidth, height: containerHeight } = containerSize;

    // check if new selected step fit to screen
    const zoomHeight = containerHeight / (highlightedNodes.by - highlightedNodes.ay);
    const adjustedZoomY = Math.min(zoomHeight * zoom, zoom);
    const zoomWidth = containerWidth / (highlightedNodes.bx - highlightedNodes.ax);
    const adjustedZoomX = Math.min(zoomWidth * zoom, zoom);
    const adjustedZoom = Math.min(adjustedZoomY, adjustedZoomX);

    if (adjustedZoom && adjustedZoom < zoom) {
      // if selected step doesn't fit to screen - set reduced zoom
      onZoomChanged(adjustedZoom);
      // recalculate translate shift for updated zoom
      highlightedNodes = getHighlightedNodes(activeNodes, adjustedZoom);
      const { deltaY, deltaX } = getTranslateXY(highlightedNodes, containerSize);
      setTranslate({
        x: translate.x + deltaX,
        y: translate.y + deltaY,
      });
    } else {
      // if new step fit to screen just shift translate to show it
      const { deltaY, deltaX } = getTranslateXY(highlightedNodes, containerSize);
      if (deltaX !== 0 || deltaY !== 0) {
        setTranslate({
          x: translate.x + deltaX,
          y: translate.y + deltaY,
        });
      }
    }
  }, [activeStep]);

  return (
    <div
      className={cn('rt-project-route-tree rx-flex-1', { 'rt-project-route-tree--nodes-disabled': nodesDisabled })}
      style={autoSize ? treeSize : {}}>
      <RsTree
        ref={treeRef}
        data={data}
        separation={SEPARATION}
        nodeSize={{
          x: SUBSTANCE_WIDTH,
          y: SUBSTANCE_HEIGHT,
        }}
        scaleExtent={{
          min: ZOOM_MIN,
          max: ZOOM_MAX,
        }}
        depthFactor={depthFactor}
        orientation={orientation}
        translate={translate}
        zoomable={zoomEnabled}
        zoom={zoom}
        onUpdate={onUpdateHandler}
        pathClassFunc={pathClassFunc}
        pathFunc={pathFunc}
        renderCustomNodeElement={renderCustomNodeElement}
        onResizeLayout={onResizeLayout}
        treeId={treeId}
        shouldResize={shouldResize}
        addNodeEnabled={addNodeEnabled}
        leafPadding={leafPadding}
      />
    </div>
  );
}

ProjectRouteTree.defaultProps = {
  zoom: ZOOM_ONE,
  onZoomChanged: () => null,
  zoomEnabled: true,
  orientation: ORIENTATION_RIGHT_TO_LEFT,
  autoSize: false,
  structureImagesPath: PATH_ROUTE_SVG,
  setActiveStep: () => null,
  setConditionsIsShown: () => null,
  onDetailsClick: () => null,
  onActiveStepChange: () => null,
  nodesDisabled: false,
  addNodeEnabled: true,
  onAddNodeClick: () => null,
  openLinkOptions: () => null,
  shouldResize: false,
  onResized: () => null,
  leafPadding: 0,
  displayStepConfidence: false,
};

ProjectRouteTree.propTypes = {
  data: PropTypes.shape({
    _type: PropTypes.string,
    _data: PropTypes.object,
    children: PropTypes.array,
  }),
  orientation: PropTypes.string,
  zoom: PropTypes.number,
  onZoomChanged: PropTypes.func,
  zoomEnabled: PropTypes.bool,
  autoSize: PropTypes.bool,
  structureImagesPath: PropTypes.string,
  onDetailsClick: PropTypes.func,
  onActiveStepChange: PropTypes.func,
  treeId: PropTypes.string,
  nodesDisabled: PropTypes.bool,
  routeType: PropTypes.oneOf(RouteType.allTypes),
  addNodeEnabled: PropTypes.bool,
  onAddNodeClick: PropTypes.func,
  openLinkOptions: PropTypes.func,
  shouldResize: PropTypes.bool,
  onResized: PropTypes.func,
  leafPadding: PropTypes.number,
  displayStepConfidence: PropTypes.bool,
  activeStep: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  setActiveStep: PropTypes.func,
  conditionsIsShown: PropTypes.bool,
  setConditionsIsShown: PropTypes.func,
};

export default ProjectRouteTree;
